/**
 * The session as text (SEO_BRIEF_10 section 2.2): what Copy puts on the clipboard and what
 * Download .txt saves, both made here from the lines on screen. Every line in both languages,
 * oldest first, each language named in the page's language:
 *
 *   English: Where is the station?
 *   Spanish: ¿Dónde está la estación?
 *
 *   Spanish: Dos calles a la derecha.
 *   English: Two streets to the right.
 *
 * A line that could not be translated has its original only; a "Hold to talk" line into
 * English, whose words were never written down in the speaker's language, has the
 * translation only. The audio is never part of it: nothing records it.
 */

export type Side = 'a' | 'b';

export type LineState = 'pending' | 'done' | 'failed' | 'limited';

/** One thing said, and its translation. */
export interface Line {
  readonly id: number;
  /** Who said it: `a` in Speak mode, either side in Conversation mode. */
  readonly side: Side;
  /** The language it was said in, and the one it is translated into. */
  readonly from: string;
  readonly to: string;
  /** The words as said; '' when only the translation is known (Hold to talk into English). */
  original: string;
  /** The translation, once there is one. */
  translation: string | null;
  /**
   * `pending` while it is translated, `done`, `failed` (shown with a short note), or `limited`
   * (the translation is unavailable for now; the limit message says so once, for every line).
   */
  state: LineState;
}

/** The session's text, as Copy and Download .txt give it. '' when there is nothing to give. */
export function sessionText(lines: readonly Line[], nameOf: (code: string) => string): string {
  const blocks: string[] = [];
  for (const line of lines) {
    const rows: string[] = [];
    const original = line.original.trim();
    const translation = (line.translation ?? '').trim();
    if (original) rows.push(`${nameOf(line.from)}: ${original}`);
    if (translation && line.state === 'done') rows.push(`${nameOf(line.to)}: ${translation}`);
    if (rows.length) blocks.push(rows.join('\n'));
  }
  return blocks.length ? `${blocks.join('\n\n')}\n` : '';
}

/** The download's name: `live-translation-2026-10-29-1432.txt`, the time it was saved. */
export function sessionFileName(at: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const day = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
  return `live-translation-${day}-${pad(at.getHours())}${pad(at.getMinutes())}.txt`;
}
